import {
  type EnergyOverpaymentRuleSet,
  type LoyaltyTaxInput,
  LoyaltyTaxInputSchema,
  type LoyaltyTaxOutput,
  type OverpaymentOutput,
  type OverpaymentResult,
  type PlanInput,
  PlanInputSchema,
  type ReferenceBand,
} from "./types.js";

// ---------------------------------------------------------------------------
// Affiliate-handoff guard (ship-condition, hard-encoded).
//
// Electricity retail is NOT a s764A financial product, so the energy-switching
// affiliate (e.g. Econnex) is a PERMITTED handoff — but the panel ship-condition
// keeps it OFF the rules calc surface entirely: this module is FACTS ONLY ("your
// plan is $X vs the DMO reference of $Y"), never "switch and save $X". The
// affiliate lives downstream in the app/marketing layer, never alongside a
// personalised reference comparison produced here.
//
// We make that executable, not doc prose: any identifier attempting to ride a
// result surface must route through `assertNoAffiliateOnResult`, which throws for
// any energy-switch / affiliate / "switch & save" identifier. The calc functions
// below carry no partner/handoff field by construction — a comparison output
// cannot reference an affiliate. The guard + its test make the rule enforceable.
// ---------------------------------------------------------------------------

/** Identifiers that may never ride alongside a personalised reference result. */
const FORBIDDEN_AFFILIATE_PATTERNS = [
  "econnex",
  "bill hero",
  "billhero",
  "affiliate",
  "switch and save",
  "switch & save",
  "switch now",
  "compare and switch",
  "referral",
  "commission",
  "lead-gen",
  "leadgen",
];

export class AffiliateOnResultError extends Error {
  constructor(identifier: string) {
    super(
      `Energy ship-condition: "${identifier}" is an energy-switch affiliate handoff and ` +
        `must not ride a personalised reference-comparison surface (this module is facts ` +
        `only — the affiliate lives downstream, never in the rules calc).`,
    );
    this.name = "AffiliateOnResultError";
  }
}

/**
 * Assert that an identifier is permitted on a reference-comparison result
 * surface. Throws `AffiliateOnResultError` for any energy-switch/affiliate
 * identifier. Returns the (clean) identifier unchanged so call sites can use it
 * inline. This is the executable form of the "affiliate off the calc" condition.
 */
export function assertNoAffiliateOnResult(identifier: string): string {
  const id = identifier.toLowerCase();
  for (const pattern of FORBIDDEN_AFFILIATE_PATTERNS) {
    if (id.includes(pattern)) throw new AffiliateOnResultError(identifier);
  }
  return identifier;
}

// ---------------------------------------------------------------------------
// Pure calculation.
// ---------------------------------------------------------------------------

/** Round to whole dollars. */
function roundDollars(n: number): number {
  return Math.round(n);
}

/** Round a fraction to 4 dp (so 10% reads as 0.1, not 0.09999…). */
function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

/**
 * Annualise a plan from its bill components: daily supply charge over 365 days +
 * usage rate × annual usage. Whole dollars.
 */
function annualiseUsage(
  dailySupplyCharge: number,
  usageRatePerKwh: number,
  annualUsageKwh: number,
): number {
  return roundDollars(
    dailySupplyCharge * 365 + usageRatePerKwh * annualUsageKwh,
  );
}

/**
 * Compare the user's current plan against the AER DMO residential reference price
 * for their distribution zone — a CITED FACTUAL COMPARISON, never a "switch and
 * save $X" claim. Pure: `inputs -> ruleset -> output`, no I/O, no clock.
 *
 * Returns an out-of-scope marker for VICTORIA (which uses the ESC's VDO, a
 * different mechanism — comparing it to a DMO figure would be wrong/misleading),
 * so callers branch rather than getting a bad number.
 */
export function overpaymentVsReference(
  rawInput: PlanInput,
  rules: EnergyOverpaymentRuleSet,
): OverpaymentResult {
  const input = PlanInputSchema.parse(rawInput);

  // Victoria is out of scope — VDO, not DMO. Flag it; do not invent a comparison.
  if (input.zone === "VICTORIA") {
    return {
      outOfScope: true,
      zone: "VICTORIA",
      reason: "victoria_uses_vdo",
    };
  }

  const zoneRef = rules.zones[input.zone];
  const referencePrice = zoneRef.annualReferencePrice;

  const currentAnnualCost =
    input.currentAnnualCost ??
    // `refine` guarantees one of the two paths is present.
    annualiseUsage(
      input.usage!.dailySupplyCharge,
      input.usage!.usageRatePerKwh,
      input.usage!.annualUsageKwh,
    );

  const differenceFromReference = roundDollars(currentAnnualCost - referencePrice);
  const percentFromReference = round4(
    (currentAnnualCost - referencePrice) / referencePrice,
  );

  let band: ReferenceBand;
  if (differenceFromReference > 0) band = "above_reference";
  else if (differenceFromReference < 0) band = "below_reference";
  else band = "at_reference";

  // ACCC-flagged "materially over the default offer" cohort: more than 10% above.
  const materiallyAbove = percentFromReference > 0.1;

  const out: OverpaymentOutput = {
    zone: input.zone,
    currentAnnualCost,
    referencePrice,
    differenceFromReference,
    percentFromReference,
    band,
    materiallyAbove,
  };
  return out;
}

/**
 * Surface the ACCC-published "loyalty tax" — the average annual gap between what
 * new-plan customers pay and what longstanding customers on old plans pay — as a
 * CITED RANGE. Pure: `inputs -> ruleset -> output`. This is a population-average
 * factual range, NOT a personalised saving (the input carries only the assessment
 * date; there is deliberately no per-user dollar figure to "save").
 */
export function loyaltyTaxEstimate(
  rawInput: LoyaltyTaxInput,
  rules: EnergyOverpaymentRuleSet,
): LoyaltyTaxOutput {
  LoyaltyTaxInputSchema.parse(rawInput);
  const { lowerAnnual, upperAnnual, lowerBasis, upperBasis } = rules.loyaltyTax;
  return { lowerAnnual, upperAnnual, lowerBasis, upperBasis };
}
